/**
 * Mode demonstration du back-office.
 *
 * Sert a produire une documentation illustree sans taper d'identifiants et
 * sans dependre d'un backend joignable. Les reponses ci-dessous sont des
 * copies exactes de ce que l'API de recette a reellement renvoye sur le
 * laboratoire de test, patients fictifs compris.
 *
 * GARDE-FOUS :
 *   1. Ne s'active que si VITE_DEMO_MODE vaut "1" au build. Un build normal
 *      ne definit pas la variable et ce fichier reste inerte.
 *   2. Ne fabrique aucun droit : il repond a la place du reseau, il ne
 *      contourne aucune regle de securite cote serveur.
 *   3. Un bandeau permanent signale le mode, pour qu'aucune capture d'ecran
 *      ne puisse etre prise pour de la production.
 */

export const MODE_DEMO = import.meta.env?.VITE_DEMO_MODE === "1";

export const BANDEAU_DEMO = "MODE DÉMONSTRATION — données fictives, aucun patient réel";

const ilYA = (minutes) => new Date(Date.now() - minutes * 60000).toISOString();

const FILE = {
  total: 6,
  exceptions: 4,
  dossiers: [
    {
      id: "demo-1",
      creeLe: ilYA(52),
      motifPrincipal: "probleme_ins",
      libelle: "Identité non qualifiable",
      gravite: 1,
      motifs: ["probleme_ins", "discordance_documentaire"],
    },
    {
      id: "demo-2",
      creeLe: ilYA(38),
      motifPrincipal: "identite_incomplete",
      libelle: "Identité incomplète",
      gravite: 2,
      motifs: ["identite_incomplete"],
    },
    {
      id: "demo-3",
      creeLe: ilYA(21),
      motifPrincipal: "identite_incertaine",
      libelle: "Identité incertaine",
      gravite: 3,
      motifs: ["identite_incertaine", "ordonnance_illisible", "analyse_inconnue"],
    },
    {
      id: "demo-4",
      creeLe: ilYA(9),
      motifPrincipal: "analyse_inconnue",
      libelle: "Analyse non reconnue",
      gravite: 6,
      motifs: ["analyse_inconnue"],
    },
  ],
};

const extraction = (id, champ, opts = {}) => ({
  id,
  champ,
  texteBrut: opts.brut ?? null,
  valeurProposee: opts.proposee ?? null,
  valeurNormalisee: opts.normalisee ?? null,
  valeurValidee: opts.validee ?? null,
  score: opts.score ?? null,
  confiance: opts.confiance ?? "non_reconnu",
  modele: "gemini",
});

const DOSSIERS = {
  "demo-1": {
    dossier: {
      id: "demo-1",
      statut: "a_verifier",
      laboratoryId: "demo",
      qualificationINS: "anomalie",
      raisonQualification:
        "Discordance significative entre les documents, validation humaine obligatoire.",
      discordancesIdentite: ["nomNaissance"],
      creeLe: ilYA(52),
    },
    documents: [
      {
        id: "doc-1",
        type: "ordonnance",
        origine: "photo",
        nombrePages: 1,
        cheminStockage: "",
      },
    ],
    extractions: [
      extraction("e1", "nomNaissance", {
        brut: "MERCIER",
        proposee: "MERCIER",
        normalisee: "MERCIER",
        score: 0.88,
        confiance: "forte",
      }),
      extraction("e2", "prenom", {
        brut: "Claire",
        proposee: "Claire",
        normalisee: "Claire",
        score: 0.88,
        confiance: "forte",
      }),
      extraction("e3", "dateNaissance", {
        brut: "03/11/1982",
        proposee: "03/11/1982",
        normalisee: "03/11/1982",
        score: 0.88,
        confiance: "forte",
      }),
      extraction("e4", "analyse.1", {
        brut: "TSH",
        proposee: { libelle: "TSH" },
        normalisee: { codeNABM: "1208", libelle: "Thyréostimuline" },
        score: 0.88,
        confiance: "forte",
      }),
    ],
    journal: [
      { id: "j1", action: "dossier.cree", auteur: "analyse_ordonnance", horodatage: ilYA(52) },
      { id: "j2", action: "document.enregistre", auteur: "analyse_ordonnance", horodatage: ilYA(52) },
      { id: "j3", action: "dossier.statut", auteur: "analyse_ordonnance", horodatage: ilYA(51) },
    ],
    exception: {
      estUneException: true,
      motifPrincipal: "probleme_ins",
      libelle: "Identité non qualifiable",
      gravite: 1,
      motifs: ["probleme_ins", "discordance_documentaire"],
    },
  },
  "demo-3": {
    dossier: {
      id: "demo-3",
      statut: "a_verifier",
      qualificationINS: "non_qualifiee",
      discordancesIdentite: [],
      qualiteOrdonnance: {
        exploitable: false,
        message: "Image trop sombre, texte non exploitable",
      },
      creeLe: ilYA(21),
    },
    documents: [
      { id: "doc-3", type: "ordonnance", origine: "photo", nombrePages: 1, cheminStockage: "" },
    ],
    extractions: [
      extraction("f1", "nomNaissance", {
        brut: "B0UCH4RD",
        proposee: "BOUCHARD",
        normalisee: "BOUCHARD",
        score: 0.22,
        confiance: "ambigu",
      }),
      extraction("f2", "analyse.1", {
        brut: "illisible",
        proposee: { libelle: "illisible" },
        score: 0.22,
        confiance: "ambigu",
      }),
    ],
    journal: [
      { id: "k1", action: "dossier.cree", auteur: "analyse_ordonnance", horodatage: ilYA(21) },
    ],
    exception: {
      estUneException: true,
      motifPrincipal: "identite_incertaine",
      libelle: "Identité incertaine",
      gravite: 3,
      motifs: ["identite_incertaine", "ordonnance_illisible", "analyse_inconnue"],
    },
  },
};

const INDICATEURS = {
  dossiers: 6,
  tempsMoyenSecondes: 105,
  tempsMedianSecondes: 105,
  dossiersHors90s: 1,
  tauxReconnaissanceAutomatique: 0.75,
  tauxCorrection: 0,
  tauxException: 0.667,
};

const FILE_ENVOI = {
  total: 3,
  parEtat: { erreur: 1, en_attente_annulation: 1, envoye: 1 },
  entrees: [
    {
      id: "q1",
      etat: "erreur",
      nomFichier: "DEMO-1001_20260814_ordonnance.jpg",
      demandeLe: ilYA(34),
      tentatives: 2,
      typeErreur: "serveur_inaccessible",
      derniereErreur: "ECONNREFUSED depot SGL injoignable",
    },
    {
      id: "q2",
      etat: "en_attente_annulation",
      nomFichier: "DEMO-1002_20260814_ordonnance.jpg",
      demandeLe: ilYA(0),
      tentatives: 0,
    },
    {
      id: "q3",
      etat: "envoye",
      nomFichier: "DEMO-1000_20260814_ordonnance.jpg",
      demandeLe: ilYA(96),
      tentatives: 0,
    },
  ],
};

/** Repond a la place du reseau. Renvoie null si la route n'est pas simulee. */
export function reponseDemo(methode, url) {
  const chemin = String(url || "").split("?")[0].replace(/^\/+/, "");

  if (chemin === "dossiers/file") return FILE;
  if (chemin === "dossiers/indicateurs") return INDICATEURS;
  if (chemin === "transmissions-file") return FILE_ENVOI;

  const detail = chemin.match(/^dossiers\/(demo-\d+)$/);
  if (detail) return DOSSIERS[detail[1]] || DOSSIERS["demo-1"];

  if (/^dossiers\/extractions\/.+\/valider$/.test(chemin)) return { corrigee: true };
  if (/^dossiers\/.+\/statut$/.test(chemin)) return { statut: "controle_secretariat" };
  if (/^transmissions-file\/[^/]+$/.test(chemin) && methode === "post") {
    return {
      id: "q4",
      nomFichier: "DEMO-1003_20260814_ordonnance.jpg",
      delaiAnnulationMs: 30000,
    };
  }
  if (/^transmissions-file\/entrees\/.+\/annuler$/.test(chemin)) return { annule: true };
  if (/^transmissions-file\/entrees\/.+\/relancer$/.test(chemin)) return { relance: true };

  return null;
}

/**
 * Reglages du laboratoire. Cet ecran lit Firestore directement, pas l'API :
 * il lui faut donc sa propre reponse simulee, sinon il reste sur « Chargement ».
 */
export const REGLAGES_DEMO = {
  champsPatient: { telephone: true, email: false, dossier: true },
  formatCodeBarres: ["25999AA99", "99999999"],
  depotSftp: {
    actif: true,
    host: "sftp.sgl-demonstration.fr",
    port: 22,
    username: "lybo-depot",
    remotePath: "/entrant/lybo",
  },
};
